import React, { useCallback, useMemo } from 'react';
import { Button } from 'components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { FileInfo, UploadState } from 'infrastructure/uploads/UploadsContext';
import OverallProgress from './OverallProgress';

const activeUploadStates: UploadState[] = ['queued', 'uploading', 'processing'];

const isActiveUpload = (file: FileInfo) =>
  activeUploadStates.includes(file.status);

export const getCurrentBatchFiles = (files: readonly FileInfo[]) => {
  const latestActiveFile = [...files].reverse().find(isActiveUpload);
  const latestFile = files[files.length - 1];
  const currentBatchId = (latestActiveFile ?? latestFile)?.batchId;

  return currentBatchId
    ? files.filter(file => file.batchId === currentBatchId)
    : [];
};

const getOverallProgress = (files: readonly FileInfo[]) => {
  const totalFileSize = files.reduce(
    (accumulator, file) => accumulator + file.size,
    0
  );

  if (totalFileSize === 0) {
    return files.every(file => !isActiveUpload(file)) ? 100 : 0;
  }

  const totalUploaded = files.reduce((accumulator, file) => {
    if (file.status === 'processing' || file.status === 'uploaded') {
      return accumulator + file.size;
    }

    return accumulator + Math.min(file.bytesUploaded, file.size);
  }, 0);

  return Number((totalUploaded / totalFileSize) * 100);
};

const getTitle = ({
  activeFiles,
  failedFiles,
  uploadedFiles,
  allFiles,
}: {
  activeFiles: number;
  failedFiles: number;
  uploadedFiles: number;
  allFiles: number;
}) => {
  if (activeFiles > 0) {
    return `Uploading ${activeFiles} ${activeFiles > 1 ? 'items' : 'item'}…`;
  }

  if (failedFiles > 0 && uploadedFiles > 0) {
    return `Uploaded ${uploadedFiles} of ${allFiles} items. ${failedFiles} failed.`;
  }

  if (failedFiles > 0) {
    return `Failed to upload ${failedFiles} ${
      failedFiles > 1 ? 'items' : 'item'
    }!`;
  }

  return allFiles === 1 ? 'Upload successful!' : `Uploaded ${allFiles} items.`;
};

export interface UploadSummaryHeaderProps {
  files: readonly FileInfo[];
  showCancelButton: boolean;
  cancelFunc?: () => void;
  clearUploads?: () => void;
}

const UploadSummaryHeader: React.FC<UploadSummaryHeaderProps> = ({
  cancelFunc,
  clearUploads,
  files,
  showCancelButton,
}) => {
  const currentBatchFiles = useMemo(() => getCurrentBatchFiles(files), [files]);
  const uploadedFilesCount = currentBatchFiles.filter(
    file => file.status === 'uploaded'
  ).length;
  const failedFilesCount = currentBatchFiles.filter(
    file => file.status === 'failed'
  ).length;
  const activeFilesCount = currentBatchFiles.filter(isActiveUpload).length;
  const hasActiveUploads = activeFilesCount > 0;
  const hasFailedFiles = failedFilesCount > 0;
  const uploadsComplete = !hasActiveUploads;
  const overallProgress = getOverallProgress(currentBatchFiles);

  const onCancel: React.MouseEventHandler = useCallback(
    e => {
      e.stopPropagation();
      cancelFunc?.();
      clearUploads?.();
    },
    [cancelFunc, clearUploads]
  );

  return (
    <div className="upload-summary-header">
      {cancelFunc && showCancelButton && (
        <Button
          aria-label="Cancel Upload"
          buttonStyle="link"
          className="cancel-btn unstyled-button"
          color="light"
          onClick={onCancel}
          title="Cancel Upload"
          icon={<FontAwesomeIcon icon={faTimes} fixedWidth />}
        />
      )}
      <h4 className="title">
        {getTitle({
          activeFiles: activeFilesCount,
          failedFiles: failedFilesCount,
          uploadedFiles: uploadedFilesCount,
          allFiles: currentBatchFiles.length,
        })}
      </h4>
      <OverallProgress
        hasActiveUploads={hasActiveUploads}
        hasFailedFiles={hasFailedFiles}
        overallProgress={overallProgress}
        uploadsComplete={uploadsComplete}
      />
    </div>
  );
};

export default UploadSummaryHeader;
